import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, Form, Alert, Breadcrumb } from 'react-bootstrap';
import { ChevronLeft, Folder, X } from 'lucide-react';
import { API_ENDPOINTS } from "../../../config/api.config";
import { DataSource, Folder as FolderType } from '../../../types/files';
import { FileIcon } from '../../data-sources/components/FileIcon';
import { apiService } from "../../../services/apiService.ts";

interface ListItem {
    id: string;
    uniqueId: string;
    name: string;
    type: 'folder' | 'file';
    metadata?: DataSource['metadata'];
}

interface FileSelectionModalProps {
    show: boolean;
    onHide: () => void;
    onSelect: (selectedFiles: string[], selectedFileSources: DataSource[]) => void;
    currentSelected: string[];
    onError?: (error: Error) => void;
}

const FileSelectionModal: React.FC<FileSelectionModalProps> = ({
                                                                   show,
                                                                   onHide,
                                                                   onSelect,
                                                                   currentSelected,
                                                                   onError
                                                               }) => {
    console.log('=== FileSelectionModal Props ===');
    console.log('show:', show);
    console.log('currentSelected:', currentSelected);

    const [files, setFiles] = useState<DataSource[]>([]);
    const [folders, setFolders] = useState<FolderType[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
    const [breadcrumbs, setBreadcrumbs] = useState<Array<{id: string, name: string}>>([]);
    const [selectedFiles, setSelectedFiles] = useState<Set<string>>(() => {
        console.log('Initializing selectedFiles with:', currentSelected);
        return new Set(currentSelected);
    });
    const [selectedFilesData, setSelectedFilesData] = useState<DataSource[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [initialized, setInitialized] = useState(false);

    // Initialize selected files when modal opens
    useEffect(() => {
        if (show) {
            console.log('=== Modal Show Effect ===');
            console.log('Current selected files before update:', currentSelected);
            setSelectedFiles(new Set(currentSelected));
            console.log('Setting selected files to:', new Set(currentSelected));

            if (!initialized) {
                console.log('Initializing modal state');
                setCurrentFolderId(null);
                setBreadcrumbs([]);
                setInitialized(false);
            }
        }
    }, [show, currentSelected]);

    // Fetch selected files data
    useEffect(() => {
        const fetchSelectedFiles = async () => {
            if (!currentSelected.length) {
                console.log('No selected files to fetch');
                return;
            }

            console.log('=== Fetching Selected Files ===');
            console.log('currentSelected:', currentSelected);

            try {
                const queryParams = new URLSearchParams({
                    ids: currentSelected.join(',')
                });

                console.log('Fetching selected files with params:', queryParams.toString());
                const response = await apiService.get<{ dataSources: DataSource[] }>(
                    `${API_ENDPOINTS.DATA_SOURCE.LIST}?${queryParams}`
                );

                if (response.dataSources) {
                    console.log('Received selected files data:', response.dataSources);
                    setSelectedFilesData(response.dataSources);
                }
            } catch (error) {
                console.error('Error fetching selected files:', error);
            }
        };

        if (show && currentSelected.length > 0) {
            fetchSelectedFiles();
        }
    }, [show, currentSelected]);

    const fetchData = async () => {
        console.log('=== Fetching Folder Data ===');
        console.log('currentFolderId:', currentFolderId);
        console.log('searchTerm:', searchTerm);

        setIsLoading(true);
        try {
            const queryParams = new URLSearchParams({
                search: searchTerm,
                ...(currentFolderId && { folder_id: currentFolderId })
            });

            console.log('Fetching with params:', queryParams.toString());

            const [filesData, foldersData] = await Promise.all([
                apiService.get<{ dataSources: DataSource[] }>(
                    `${API_ENDPOINTS.DATA_SOURCE.LIST}?${queryParams}`
                ),
                apiService.get<{ folders: FolderType[] }>(
                    `${API_ENDPOINTS.DATA_SOURCE.LIST_FOLDERS}${currentFolderId ? `/${currentFolderId}` : ''}`
                )
            ]);

            console.log('Received files data:', filesData);
            console.log('Received folders data:', foldersData);

            const fileItems = (filesData.dataSources as DataSource[]).filter(
                (item): item is DataSource & { item_type?: 'file' } =>
                    !item.item_type || item.item_type === 'file'
            );

            console.log('Filtered file items:', fileItems);
            setFiles(fileItems);
            setFolders(foldersData.folders || []);

            if (!initialized && foldersData.folders) {
                const homeFolder = foldersData.folders.find((folder: FolderType) => folder.name === 'Home');
                if (homeFolder) {
                    console.log('Found home folder:', homeFolder);
                    handleFolderClick(homeFolder.id, homeFolder.name);
                    setInitialized(true);
                }
            }
        } catch (error: unknown) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to fetch data sources';
            console.error('Error fetching data:', error);
            setError(errorMessage);
            if (onError) {
                onError(new Error(errorMessage));
            }
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (show) {
            console.log('=== Fetch Data Effect Triggered ===');
            console.log('show:', show);
            console.log('currentFolderId:', currentFolderId);
            console.log('searchTerm:', searchTerm);
            fetchData();
        }
    }, [show, currentFolderId, searchTerm]);

    const sortedItems = React.useMemo(() => {
        console.log('=== Computing Sorted Items ===');
        console.log('Current folders:', folders);
        console.log('Current files:', files);

        const folderItems: ListItem[] = folders.map((folder: FolderType) => ({
            ...folder,
            type: 'folder' as const,
            uniqueId: `folder_${folder.id}`
        }));

        const fileItems: ListItem[] = files.map((file: DataSource) => ({
            ...file,
            type: 'file' as const,
            uniqueId: `file_${file.id}`
        }));

        const sorted = [...folderItems, ...fileItems].sort((a, b) => {
            if (a.type !== b.type) {
                return a.type === 'folder' ? -1 : 1;
            }
            return a.name.localeCompare(b.name);
        });

        console.log('Sorted items:', sorted);
        return sorted;
    }, [folders, files]);

    const handleFolderClick = (folderId: string, folderName: string) => {
        console.log('=== Folder Click ===');
        console.log('Clicking folder:', { folderId, folderName });
        setCurrentFolderId(folderId);
        setBreadcrumbs(prev => [...prev, { id: folderId, name: folderName }]);
    };

    const handleNavigateToFolder = (index: number) => {
        console.log('=== Navigate to Folder ===');
        console.log('Navigating to index:', index);
        if (index === -1) {
            console.log('Navigating to root');
            setCurrentFolderId(null);
            setBreadcrumbs([]);
            return;
        }
        const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
        const targetFolder = newBreadcrumbs[newBreadcrumbs.length - 1].id;
        console.log('New breadcrumbs:', newBreadcrumbs);
        console.log('Target folder:', targetFolder);
        setCurrentFolderId(targetFolder);
        setBreadcrumbs(newBreadcrumbs);
    };

    const handleFileSelection = (fileId: string) => {
        console.log('=== File Selection ===');
        console.log('Toggling file:', fileId);
        console.log('Current selected files:', Array.from(selectedFiles));

        setSelectedFiles(prev => {
            const newSet = new Set(prev);
            if (newSet.has(fileId)) {
                console.log('Removing file:', fileId);
                newSet.delete(fileId);
            } else {
                console.log('Adding file:', fileId);
                newSet.add(fileId);
            }
            console.log('New selected files:', Array.from(newSet));
            return newSet;
        });
    };

    const handleSave = () => {
        console.log('=== Saving Selection ===');
        console.log('Selected files:', Array.from(selectedFiles));
        console.log('Selected files data:', selectedFilesData);
        onSelect(Array.from(selectedFiles), selectedFilesData);
        onHide();
    };

    const getMimeType = (item: ListItem): string => {
        return (item.metadata?.original_metadata?.mime_type ||
            item.metadata?.original_metadata?.mimeType ||
            'application/octet-stream').toLowerCase();
    };

    // Debug log render
    console.log('=== Rendering Modal ===');
    console.log('Current files:', files);
    console.log('Selected files:', Array.from(selectedFiles));
    console.log('Selected files data:', selectedFilesData);
    console.log('Current folder ID:', currentFolderId);
    console.log('Breadcrumbs:', breadcrumbs);

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Select Data Sources</Modal.Title>
            </Modal.Header>
            <Modal.Body className="position-relative">
                {isLoading && (
                    <div className="position-absolute w-100 h-100 top-0 start-0 d-flex align-items-center justify-content-center bg-white bg-opacity-75">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                )}

                {error && <Alert variant="danger">{error}</Alert>}

                <Form.Control
                    type="text"
                    placeholder="Search files..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="mb-3"
                />

                {selectedFilesData.length > 0 && (
                    <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <small className="text-muted">
                                Selected Files ({selectedFilesData.length})
                            </small>
                        </div>
                        <div className="border rounded">
                            <div className="p-2">
                                {selectedFilesData.map(file => (
                                    <div
                                        key={`selected-${file.id}`}
                                        className="d-flex align-items-center justify-content-between py-1"
                                    >
                                        <div className="d-flex align-items-center">
                                            <FileIcon
                                                mimeType={getMimeType({
                                                    ...file,
                                                    type: 'file',
                                                    uniqueId: `file_${file.id}`
                                                })}
                                                size={20}
                                                className="me-2 text-secondary"
                                            />
                                            <span>{file.name}</span>
                                        </div>
                                        <Button
                                            variant="link"
                                            className="text-danger p-0"
                                            onClick={() => handleFileSelection(file.id)}
                                        >
                                            <X size={16} />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {breadcrumbs.length > 0 && (
                    <Breadcrumb className="mb-3">
                        <Breadcrumb.Item
                            onClick={() => handleNavigateToFolder(-1)}
                            style={{ cursor: 'pointer' }}
                        >
                            Root
                        </Breadcrumb.Item>
                        {breadcrumbs.map((crumb, index) => (
                            <Breadcrumb.Item
                                key={crumb.id}
                                onClick={() => handleNavigateToFolder(index)}
                                active={index === breadcrumbs.length - 1}
                                style={{ cursor: 'pointer' }}
                            >
                                {crumb.name}
                            </Breadcrumb.Item>
                        ))}
                    </Breadcrumb>
                )}

                <Table hover responsive>
                    <thead>
                    <tr>
                        <th style={{ width: '30px' }}></th>
                        <th>Name</th>
                        <th>Type</th>
                    </tr>
                    </thead>
                    <tbody>
                    {breadcrumbs.length > 0 && (
                        <tr
                            onClick={() => handleNavigateToFolder(breadcrumbs.length - 2)}
                            style={{ cursor: 'pointer' }}
                        >
                            <td></td>
                            <td>
                                <div className="d-flex align-items-center">
                                    <ChevronLeft size={20} className="me-2" />
                                    ..
                                </div>
                            </td>
                            <td>Folder</td>
                        </tr>
                    )}
                    {sortedItems.map(item => {
                        console.log('Rendering item:', item);
                        console.log('Is selected:', selectedFiles.has(item.id));
                        return (
                            <tr
                                key={item.uniqueId}
                                onClick={() => item.type === 'folder' ?
                                    handleFolderClick(item.id, item.name) :
                                    handleFileSelection(item.id)
                                }
                                className={item.type === 'file' && selectedFiles.has(item.id) ? 'table-primary' : ''}
                                style={{ cursor: 'pointer' }}
                            >
                                <td>
                                    {item.type === 'file' && (
                                        <Form.Check
                                            type="checkbox"
                                            checked={selectedFiles.has(item.id)}
                                            onChange={() => handleFileSelection(item.id)}
                                            onClick={e => e.stopPropagation()}
                                        />
                                    )}
                                </td>
                                <td>
                                    <div className="d-flex align-items-center">
                                        {item.type === 'folder' ? (
                                            <Folder size={20} className="me-2 text-primary"/>
                                        ) : (
                                            <FileIcon
                                                mimeType={getMimeType(item)}
                                                size={20}
                                                className="me-2 text-secondary"
                                            />
                                        )}
                                        {item.name}
                                    </div>
                                </td>
                                <td>{item.type === 'folder' ? 'Folder' : 'File'}</td>
                            </tr>
                        );
                    })}
                    </tbody>
                </Table>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={selectedFiles.size === 0}
                >
                    Select ({selectedFiles.size}) Files
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default FileSelectionModal;